import type { AnyAction, Reducer } from "redux";
import type { ConversationTurn, AppFileTree, TsDiagnostic, EsbuildErrorInfo, BuildStatusPayload, StudioStatusEvent, StudioMessageAttachment, StudioEvent, StudioAppTypeValue, UberReviewProjection, McpToolBlockedEventMessage } from "../types";
import type { ChatOption } from "../utils/chatOptions";
import { StudioAppType } from "../types";
import { MAX_STATUS_EVENTS, MAX_EVENTS } from "../constants";

const MAX_TEST_LOG_ENTRIES = 200;

export interface StudioTestLogEntry extends McpToolBlockedEventMessage {
    id: string;
}

interface StudioSessionState {
    appId: number | null;
    appVersionId: number | null;
    appName: string | null;
    appVersion: number | null;
    appType: StudioAppTypeValue;
    widgetType: number | null;
    widgetActionName: string | null;
    iconUrl: string | null;
    isPublished: boolean;
    /** Version-row id of the currently published version (null = unpublished). */
    publishedVersionId: number | null;
    isReadOnly: boolean;
    /** Row id of the app's newest version. When it differs from appVersionId the workbench is
     *  behind latest (previewing an old version, or another user just created a newer one). */
    latestVersionId: number | null;
}

interface StudioBundleState {
    fileTree: AppFileTree | null;
    bundledIife: string | null;
    bundledCss: string | null;
    bundleError: string | null;
    /**
     * True when bundledIife is an instrumented build (carries inline-edit data-sedit attributes).
     * The workbench preview renders instrumented bundles whenever available so toggling edit mode
     * is a pure postMessage; persisted/published bundles are always clean (server-enforced).
     */
    bundleInstrumented: boolean;
}

interface StudioStreamState {
    history: ConversationTurn[];
    thought: string;
    streamingMessage: string;
    isStreaming: boolean;
    progressLabel: string | null;
    error: string | null;

    liveStatusEvents: StudioStatusEvent[];
    streamingEvents: StudioEvent[];

    buildStatus: string | null;
    buildError: string | null;
    buildAttempt: number | null;
    buildDiagnosticHistory: Array<{
        attempt: number;
        diagnostics: TsDiagnostic[];
        esbuildErrors: EsbuildErrorInfo[];
        status: "success" | "failed";
    }>;
}

interface StudioWorkspaceState {
    selectedModelKey: string | null;
    previewReloadKey: number;
    viewAsRole: string | null;
    viewMode: "full" | "mobile";
    /** Tracks the latest review row for the currently open app (null when none). */
    currentReview: UberReviewProjection | null;
    /** Inline text editing of the preview is active (renders the instrumented bundle, text is contenteditable). */
    editTextMode: boolean;
    /** Transient "another user created a new version" ping; null when dismissed/none. */
    remoteVersionNotice: { version: number; byName: string | null } | null;
    /** Bumped whenever a version is created remotely so an open versions list re-fetches. */
    versionListRefreshKey: number;
    /** Inline text edits persist optimistically in a serialized chain; this counts the writes
     *  still in flight so the header can show a saving state before leaving edit mode. */
    pendingTextCommits: number;
}

interface StudioPermsState {
    /** Mirrored from AppDetail so the workbench can pick the right action button without an extra fetch. */
    isStudioAdmin: boolean;
    /** The configured Studio admin user IDs (loaded once when Studio mounts via getSettings). */
    studioAdminUserIds: number[];
    isSystemAdministrator: boolean;
}

interface StudioTestLogState {
    entries: StudioTestLogEntry[];
    unreadCount: number;
}

export interface IStudioState {
    session: StudioSessionState;
    bundle: StudioBundleState;
    stream: StudioStreamState;
    workspace: StudioWorkspaceState;
    perms: StudioPermsState;
    testLog: StudioTestLogState;
}

const initialSession: StudioSessionState = {
    appId: null,
    appVersionId: null,
    appName: null,
    appVersion: null,
    appType: StudioAppType.CustomApp,
    widgetType: null,
    widgetActionName: null,
    iconUrl: null,
    isPublished: false,
    publishedVersionId: null,
    isReadOnly: false,
    latestVersionId: null,
};

const initialBundle: StudioBundleState = {
    fileTree: null,
    bundledIife: null,
    bundledCss: null,
    bundleError: null,
    bundleInstrumented: false,
};

const initialStream: StudioStreamState = {
    history: [],
    thought: "",
    streamingMessage: "",
    isStreaming: false,
    progressLabel: null,
    error: null,
    liveStatusEvents: [],
    streamingEvents: [],
    buildStatus: null,
    buildError: null,
    buildAttempt: null,
    buildDiagnosticHistory: [],
};

const initialWorkspace: StudioWorkspaceState = {
    selectedModelKey: null,
    previewReloadKey: 0,
    viewAsRole: null,
    viewMode: "full",
    currentReview: null,
    editTextMode: false,
    remoteVersionNotice: null,
    versionListRefreshKey: 0,
    pendingTextCommits: 0,
};

const initialPerms: StudioPermsState = {
    isStudioAdmin: false,
    studioAdminUserIds: [],
    isSystemAdministrator: false,
};

const initialTestLog: StudioTestLogState = {
    entries: [],
    unreadCount: 0,
};

const initialState: IStudioState = {
    session: initialSession,
    bundle: initialBundle,
    stream: initialStream,
    workspace: initialWorkspace,
    perms: initialPerms,
    testLog: initialTestLog,
};

const STREAM_RESET_PATCH: Partial<StudioStreamState> = {
    thought: "",
    streamingMessage: "",
    isStreaming: true,
    progressLabel: null,
    error: null,
    buildStatus: null,
    buildError: null,
    buildAttempt: null,
    buildDiagnosticHistory: [],
    liveStatusEvents: [],
    streamingEvents: [],
};

const ADD_USER_MESSAGE = "studio/addUserMessage";
const APPEND_THOUGHT = "studio/appendThought";
const APPEND_MESSAGE = "studio/appendMessage";
const APPEND_EVENT = "studio/appendEvent";
const SET_PROGRESS_LABEL = "studio/setProgressLabel";
const STREAM_COMPLETE = "studio/streamComplete";
const STREAM_CANCELLED = "studio/streamCancelled";
const SET_APP_SESSION = "studio/setAppSession";
const SET_FILE_TREE = "studio/setFileTree";
const SET_WIDGET_BINDING = "studio/setWidgetBinding";
const SET_IS_PUBLISHED = "studio/setIsPublished";
const SET_PUBLISHED_VERSION_ID = "studio/setPublishedVersionId";
const SET_ICON_URL = "studio/setIconUrl";
const SET_ERROR = "studio/setError";
const CLEAR_ERROR = "studio/clearError";
const SET_BUILD_STATUS = "studio/setBuildStatus";
const UPDATE_FILE = "studio/updateFile";
const UPDATE_BUNDLE = "studio/updateBundle";
const LOAD_HISTORY_PAGE = "studio/loadHistoryPage";
const PREPEND_HISTORY_PAGE = "studio/prependHistoryPage";
const RESET_CONVERSATION = "studio/resetConversation";
const SET_SELECTED_MODEL = "studio/setSelectedModel";
const SET_APP_NAME = "studio/setAppName";
const APPLY_REMOTE_WORKBENCH_MESSAGE = "studio/applyRemoteWorkbenchMessage";
const REMOTE_STREAMING_STARTED = "studio/remoteStreamingStarted";
const REMOTE_STREAMING_ENDED = "studio/remoteStreamingEnded";
const RELOAD_PREVIEW = "studio/reloadPreview";
const SET_VIEW_AS_ROLE = "studio/setViewAsRole";
const SET_VIEW_MODE = "studio/setViewMode";
const SET_CURRENT_REVIEW = "studio/setCurrentReview";
const SET_IS_STUDIO_ADMIN = "studio/setIsStudioAdmin";
const SET_STUDIO_ADMIN_CONTEXT = "studio/setStudioAdminContext";
const APPEND_TEST_LOG_ENTRY = "studio/appendTestLogEntry";
const MARK_TEST_LOG_READ = "studio/markTestLogRead";
const CLEAR_TEST_LOG = "studio/clearTestLog";
const SET_EDIT_TEXT_MODE = "studio/setEditTextMode";
const REMOTE_VERSION_CREATED = "studio/remoteVersionCreated";
const DISMISS_REMOTE_VERSION_NOTICE = "studio/dismissRemoteVersionNotice";
const TEXT_COMMIT_STARTED = "studio/textCommitStarted";
const TEXT_COMMIT_FINISHED = "studio/textCommitFinished";

type AddUserMessagePayload = {
    content: string;
    attachments?: StudioMessageAttachment[];
    userId: number;
    userDisplayName: string;
    userAvatarUrl?: string;
    isOption?: boolean;
};
type StreamCompletePayload = {
    message: string;
    thought?: string;
    userId: number;
    userDisplayName: string;
    userAvatarUrl?: string;
    /** Server-issued options (with ids) from the done event, for the finished turn. */
    options?: ChatOption[];
};
type SetAppSessionPayload = {
    appId: number;
    appVersionId: number;
    appName?: string | null;
    appVersion?: number | null;
    latestVersionId?: number | null;
};
type SetFileTreePayload = {
    appId: number;
    appVersionId: number;
    fileTree: AppFileTree;
    bundledIife?: string | null;
    bundledCss?: string | null;
    bundleError?: string | null;
    appName?: string | null;
    appVersion?: number | null;
    appType?: StudioAppTypeValue;
    widgetType?: number | null;
    widgetActionName?: string | null;
    iconUrl?: string | null;
    isPublished?: boolean;
    publishedVersionId?: number | null;
    isReadOnly?: boolean;
    latestVersionId?: number | null;
};
type SetWidgetBindingPayload = { widgetType: number | null; widgetActionName: string | null };
type UpdateFilePayload = { path: string; content: string };
type UpdateBundlePayload = { iife: string; css?: string | null; instrumented?: boolean };
type SetStudioAdminContextPayload = {
    isStudioAdmin: boolean;
    isSystemAdministrator: boolean;
    studioAdminUserIds: number[];
};
// Pure UI flag — the instrumented bundle itself flows through UPDATE_BUNDLE with instrumented:true.
type SetEditTextModePayload = { enabled: boolean };
// Another user created a new version: advance latestVersionId (→ behind-latest read-only),
// surface a ping, and refresh any open versions list. appVersionId is the new version's row id.
type RemoteVersionCreatedPayload = { appVersionId: number; version: number; byName: string | null };

export const addUserMessage = (payload: AddUserMessagePayload) => ({ type: ADD_USER_MESSAGE, payload });
export const appendThought = (payload: string) => ({ type: APPEND_THOUGHT, payload });
export const appendMessage = (payload: string) => ({ type: APPEND_MESSAGE, payload });
export const appendEvent = (payload: StudioEvent) => ({ type: APPEND_EVENT, payload });
export const setProgressLabel = (payload: string) => ({ type: SET_PROGRESS_LABEL, payload });
export const streamComplete = (payload: StreamCompletePayload) => ({ type: STREAM_COMPLETE, payload });
export const streamCancelled = (payload: { userId: number }) => ({ type: STREAM_CANCELLED, payload });
export const setAppSession = (payload: SetAppSessionPayload) => ({ type: SET_APP_SESSION, payload });
export const setFileTree = (payload: SetFileTreePayload) => ({ type: SET_FILE_TREE, payload });
export const setWidgetBinding = (payload: SetWidgetBindingPayload) => ({ type: SET_WIDGET_BINDING, payload });
export const setIsPublished = (payload: boolean) => ({ type: SET_IS_PUBLISHED, payload });
export const setPublishedVersionId = (payload: number | null) => ({ type: SET_PUBLISHED_VERSION_ID, payload });
export const setIconUrl = (payload: string | null) => ({ type: SET_ICON_URL, payload });
export const setError = (payload: string) => ({ type: SET_ERROR, payload });
export const clearError = () => ({ type: CLEAR_ERROR });
export const setBuildStatus = (payload: BuildStatusPayload) => ({ type: SET_BUILD_STATUS, payload });
export const updateFile = (payload: UpdateFilePayload) => ({ type: UPDATE_FILE, payload });
export const updateBundle = (payload: UpdateBundlePayload) => ({ type: UPDATE_BUNDLE, payload });
export const loadHistoryPage = (payload: ConversationTurn[]) => ({ type: LOAD_HISTORY_PAGE, payload });
export const prependHistoryPage = (payload: ConversationTurn[]) => ({ type: PREPEND_HISTORY_PAGE, payload });
export const resetConversation = () => ({ type: RESET_CONVERSATION });
export const setSelectedModel = (payload: string) => ({ type: SET_SELECTED_MODEL, payload });
export const setAppName = (payload: string) => ({ type: SET_APP_NAME, payload });
export const applyRemoteWorkbenchMessage = (payload: ConversationTurn) => ({ type: APPLY_REMOTE_WORKBENCH_MESSAGE, payload });
export const remoteStreamingStarted = () => ({ type: REMOTE_STREAMING_STARTED });
export const remoteStreamingEnded = () => ({ type: REMOTE_STREAMING_ENDED });
export const reloadPreview = () => ({ type: RELOAD_PREVIEW });
export const setViewAsRole = (payload: string | null) => ({ type: SET_VIEW_AS_ROLE, payload });
export const setViewMode = (payload: "full" | "mobile") => ({ type: SET_VIEW_MODE, payload });
export const setCurrentReview = (payload: UberReviewProjection | null) => ({ type: SET_CURRENT_REVIEW, payload });
export const setIsStudioAdmin = (payload: boolean) => ({ type: SET_IS_STUDIO_ADMIN, payload });
export const setStudioAdminContext = (payload: SetStudioAdminContextPayload) => ({ type: SET_STUDIO_ADMIN_CONTEXT, payload });
export const appendTestLogEntry = (payload: McpToolBlockedEventMessage) => ({ type: APPEND_TEST_LOG_ENTRY, payload });
export const markTestLogRead = () => ({ type: MARK_TEST_LOG_READ });
export const clearTestLog = () => ({ type: CLEAR_TEST_LOG });
export const setEditTextMode = (payload: SetEditTextModePayload) => ({ type: SET_EDIT_TEXT_MODE, payload });
export const remoteVersionCreated = (payload: RemoteVersionCreatedPayload) => ({ type: REMOTE_VERSION_CREATED, payload });
export const dismissRemoteVersionNotice = () => ({ type: DISMISS_REMOTE_VERSION_NOTICE });
export const textCommitStarted = () => ({ type: TEXT_COMMIT_STARTED });
export const textCommitFinished = () => ({ type: TEXT_COMMIT_FINISHED });

export const studioReducer: Reducer<IStudioState, AnyAction> = (state = initialState, action) => {
    switch (action.type) {
        case ADD_USER_MESSAGE: {
            const payload = action.payload as AddUserMessagePayload;
            const last = state.stream.history[state.stream.history.length - 1];
            const nextIndex = last ? last.orderIndex + 1 : 0;
            const newTurn: ConversationTurn = {
                orderIndex: nextIndex,
                role: "user",
                content: payload.content,
                attachments: payload.attachments && payload.attachments.length > 0 ? payload.attachments : undefined,
                createdAt: new Date().toISOString(),
                userId: payload.userId,
                userDisplayName: payload.userDisplayName,
                userAvatarUrl: payload.userAvatarUrl,
                isOption: payload.isOption,
            };
            return {
                ...state,
                stream: {
                    ...state.stream,
                    ...STREAM_RESET_PATCH,
                    history: [...state.stream.history, newTurn],
                },
            };
        }

        case APPEND_THOUGHT:
            return {
                ...state,
                stream: {
                    ...state.stream,
                    thought: state.stream.thought + (action.payload as string),
                    progressLabel: null,
                },
            };

        case APPEND_MESSAGE:
            return {
                ...state,
                stream: {
                    ...state.stream,
                    streamingMessage: state.stream.streamingMessage + (action.payload as string),
                    progressLabel: null,
                },
            };

        case APPEND_EVENT: {
            const incoming = action.payload as StudioEvent;
            const idx = state.stream.streamingEvents.findIndex(c => c.id === incoming.id);
            let nextEvents: StudioEvent[];
            if (idx >= 0) {
                nextEvents = state.stream.streamingEvents.slice();
                nextEvents[idx] = incoming;
            } else {
                if (state.stream.streamingEvents.length >= MAX_EVENTS) return state;
                nextEvents = [...state.stream.streamingEvents, incoming];
            }
            return {
                ...state,
                stream: {
                    ...state.stream,
                    streamingEvents: nextEvents,
                    progressLabel: null,
                },
            };
        }

        case SET_PROGRESS_LABEL:
            return {
                ...state,
                stream: { ...state.stream, progressLabel: action.payload as string },
            };

        case STREAM_COMPLETE: {
            const payload = action.payload as StreamCompletePayload;
            const content = payload.message;
            const thought = payload.thought?.trim();
            let history = state.stream.history;
            if (content || thought) {
                const last = history[history.length - 1];
                const nextIndex = last ? last.orderIndex + 1 : 0;
                const newTurn: ConversationTurn = {
                    orderIndex: nextIndex,
                    role: "assistant",
                    content,
                    thought: thought || undefined,
                    createdAt: new Date().toISOString(),
                    toolCalls: state.stream.streamingEvents.length > 0 ? state.stream.streamingEvents : undefined,
                    userId: payload.userId,
                    userDisplayName: payload.userDisplayName,
                    userAvatarUrl: payload.userAvatarUrl,
                    options: payload.options?.length ? payload.options : undefined,
                };
                history = [...history, newTurn];
            }
            return {
                ...state,
                stream: {
                    ...state.stream,
                    isStreaming: false,
                    history,
                    streamingEvents: [],
                },
            };
        }

        case STREAM_CANCELLED: {
            // A cancelled turn is never persisted server-side, so the optimistic user
            // message must not linger locally — it would silently vanish on reload.
            // ChatPanel restores the text into the composer, making cancel an "un-send".
            const payload = action.payload as { userId: number };
            let history = state.stream.history;
            const last = history[history.length - 1];
            if (last && last.role === "user" && last.userId === payload.userId) {
                history = history.slice(0, -1);
            }
            return {
                ...state,
                stream: {
                    ...state.stream,
                    isStreaming: false,
                    history,
                    streamingEvents: [],
                },
            };
        }

        case SET_APP_SESSION: {
            const payload = action.payload as SetAppSessionPayload;
            return {
                ...state,
                session: {
                    ...state.session,
                    appId: payload.appId,
                    appVersionId: payload.appVersionId,
                    appName: payload.appName ?? state.session.appName,
                    appVersion: payload.appVersion ?? state.session.appVersion,
                    // A session created locally IS the latest; callers pass the new version's id.
                    latestVersionId: payload.latestVersionId ?? payload.appVersionId,
                },
            };
        }

        case SET_FILE_TREE: {
            const payload = action.payload as SetFileTreePayload;
            return {
                ...state,
                bundle: {
                    ...state.bundle,
                    fileTree: payload.fileTree,
                    bundledIife: payload.bundledIife ?? null,
                    bundledCss: payload.bundledCss ?? null,
                    bundleError: payload.bundleError ?? null,
                    // Loads carry the persisted bundle, which is always the clean build.
                    bundleInstrumented: false,
                },
                session: {
                    ...state.session,
                    appId: payload.appId,
                    appVersionId: payload.appVersionId,
                    appName: payload.appName ?? null,
                    appVersion: payload.appVersion ?? null,
                    appType: payload.appType ?? StudioAppType.CustomApp,
                    widgetType: payload.widgetType ?? null,
                    widgetActionName: payload.widgetActionName ?? null,
                    iconUrl: payload.iconUrl ?? null,
                    isPublished: payload.isPublished ?? false,
                    publishedVersionId: payload.publishedVersionId ?? null,
                    isReadOnly: payload.isReadOnly ?? false,
                    // Fallback to the loaded version's id: a plain /edit load returns the latest,
                    // and a local turn's just-created version IS the latest. Server sends the real
                    // latest id when previewing an older version.
                    latestVersionId: payload.latestVersionId ?? payload.appVersionId,
                },
                workspace: { ...state.workspace, editTextMode: false },
            };
        }

        case SET_WIDGET_BINDING: {
            const payload = action.payload as SetWidgetBindingPayload;
            return {
                ...state,
                session: {
                    ...state.session,
                    widgetType: payload.widgetType,
                    widgetActionName: payload.widgetActionName,
                },
            };
        }

        case SET_IS_PUBLISHED:
            return {
                ...state,
                session: { ...state.session, isPublished: action.payload as boolean },
            };

        case SET_PUBLISHED_VERSION_ID:
            return {
                ...state,
                session: { ...state.session, publishedVersionId: action.payload as number | null },
            };

        case SET_ICON_URL:
            return {
                ...state,
                session: { ...state.session, iconUrl: action.payload as string | null },
            };

        case SET_ERROR:
            return {
                ...state,
                stream: { ...state.stream, isStreaming: false, error: action.payload as string },
            };

        case CLEAR_ERROR:
            return {
                ...state,
                stream: { ...state.stream, error: null },
            };

        case SET_BUILD_STATUS: {
            const payload = action.payload as BuildStatusPayload;
            const { status, error, attempt, diagnostics, esbuildErrors } = payload;
            const hasTsDiags = diagnostics && diagnostics.length > 0;
            const hasEsbuildErrs = esbuildErrors && esbuildErrors.length > 0;

            let buildDiagnosticHistory = state.stream.buildDiagnosticHistory;
            if (attempt && (hasTsDiags || hasEsbuildErrs)) {
                buildDiagnosticHistory = [
                    ...buildDiagnosticHistory,
                    {
                        attempt,
                        diagnostics: diagnostics ?? [],
                        esbuildErrors: esbuildErrors ?? [],
                        status: status === "failed" ? "failed" : "success",
                    },
                ];
            }

            const buildStatus = (status === "building" || status === "failed" || status === "success")
                ? status as "building" | "failed" | "success"
                : undefined;

            let liveStatusEvents = state.stream.liveStatusEvents;
            if (liveStatusEvents.length < MAX_STATUS_EVENTS) {
                liveStatusEvents = [
                    ...liveStatusEvents,
                    {
                        kind: "build",
                        status: buildStatus,
                        attempt: attempt ?? undefined,
                        error: error ?? undefined,
                    },
                ];
            }

            return {
                ...state,
                stream: {
                    ...state.stream,
                    buildStatus: status,
                    buildError: error ?? null,
                    buildAttempt: attempt ?? null,
                    buildDiagnosticHistory,
                    liveStatusEvents,
                },
            };
        }

        case UPDATE_FILE: {
            const payload = action.payload as UpdateFilePayload;
            if (!state.bundle.fileTree) return state;
            return {
                ...state,
                bundle: {
                    ...state.bundle,
                    fileTree: { ...state.bundle.fileTree, [payload.path]: payload.content },
                },
            };
        }

        case UPDATE_BUNDLE: {
            const payload = action.payload as UpdateBundlePayload;
            return {
                ...state,
                bundle: {
                    ...state.bundle,
                    bundledIife: payload.iife,
                    bundledCss: payload.css ?? null,
                    bundleError: null,
                    bundleInstrumented: payload.instrumented === true,
                },
            };
        }

        case LOAD_HISTORY_PAGE:
            return {
                ...state,
                stream: {
                    ...state.stream,
                    history: action.payload as ConversationTurn[],
                    thought: "",
                    streamingMessage: "",
                    isStreaming: false,
                    error: null,
                },
            };

        case PREPEND_HISTORY_PAGE: {
            const page = action.payload as ConversationTurn[];
            if (page.length === 0) return state;
            return {
                ...state,
                stream: {
                    ...state.stream,
                    history: [...page, ...state.stream.history],
                },
            };
        }

        case RESET_CONVERSATION:
            // Reset everything EXCEPT perms. The Studio-admin context is loaded once at
            // StudioRootView mount and applies across every route in Studio; wiping it
            // when entering /studio/apps/create would make the admin sidebar icons
            // disappear until the next page reload.
            return {
                ...state,
                session: initialSession,
                bundle: initialBundle,
                stream: initialStream,
                workspace: initialWorkspace,
                testLog: initialTestLog,
            };

        case SET_SELECTED_MODEL:
            return {
                ...state,
                workspace: { ...state.workspace, selectedModelKey: action.payload as string },
            };

        case SET_APP_NAME:
            return {
                ...state,
                session: { ...state.session, appName: action.payload as string },
            };

        case APPLY_REMOTE_WORKBENCH_MESSAGE: {
            const incoming = action.payload as ConversationTurn;
            const idx = state.stream.history.findIndex(t => t.orderIndex === incoming.orderIndex);
            let history: ConversationTurn[];
            if (idx >= 0) {
                history = state.stream.history.slice();
                history[idx] = incoming;
            } else {
                history = [...state.stream.history, incoming];
            }
            return {
                ...state,
                stream: { ...state.stream, history },
            };
        }

        case REMOTE_STREAMING_STARTED:
            return {
                ...state,
                stream: { ...state.stream, ...STREAM_RESET_PATCH },
            };

        case REMOTE_STREAMING_ENDED:
            return {
                ...state,
                stream: {
                    ...state.stream,
                    isStreaming: false,
                    thought: "",
                    streamingMessage: "",
                    progressLabel: null,
                    streamingEvents: [],
                },
            };

        case RELOAD_PREVIEW:
            return {
                ...state,
                workspace: { ...state.workspace, previewReloadKey: state.workspace.previewReloadKey + 1 },
            };

        case SET_VIEW_AS_ROLE: {
            const role = action.payload as string | null;
            if (state.workspace.viewAsRole === role) return state;
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    viewAsRole: role,
                    previewReloadKey: state.workspace.previewReloadKey + 1,
                },
            };
        }

        case SET_VIEW_MODE:
            return {
                ...state,
                workspace: { ...state.workspace, viewMode: action.payload as "full" | "mobile" },
            };

        case SET_CURRENT_REVIEW:
            return {
                ...state,
                workspace: { ...state.workspace, currentReview: action.payload as UberReviewProjection | null },
            };

        case SET_IS_STUDIO_ADMIN:
            return {
                ...state,
                perms: { ...state.perms, isStudioAdmin: action.payload as boolean },
            };

        case SET_STUDIO_ADMIN_CONTEXT: {
            const payload = action.payload as SetStudioAdminContextPayload;
            return {
                ...state,
                perms: {
                    isStudioAdmin: payload.isStudioAdmin,
                    isSystemAdministrator: payload.isSystemAdministrator,
                    studioAdminUserIds: payload.studioAdminUserIds,
                },
            };
        }

        case APPEND_TEST_LOG_ENTRY: {
            const incoming = action.payload as McpToolBlockedEventMessage;
            const entry: StudioTestLogEntry = {
                ...incoming,
                id: `${incoming.occurredAt}-${state.testLog.entries.length}`,
            };
            const next = [entry, ...state.testLog.entries].slice(0, MAX_TEST_LOG_ENTRIES);
            return {
                ...state,
                testLog: { entries: next, unreadCount: state.testLog.unreadCount + 1 },
            };
        }

        case MARK_TEST_LOG_READ:
            if (state.testLog.unreadCount === 0) return state;
            return { ...state, testLog: { ...state.testLog, unreadCount: 0 } };

        case CLEAR_TEST_LOG:
            if (state.testLog.entries.length === 0 && state.testLog.unreadCount === 0) return state;
            return { ...state, testLog: initialTestLog };

        case SET_EDIT_TEXT_MODE: {
            const payload = action.payload as SetEditTextModePayload;
            // Deliberately no previewReloadKey bump: the toggle must not remount the iframe.
            // AppRenderer relays the flag to the already-running app via postMessage.
            return {
                ...state,
                workspace: { ...state.workspace, editTextMode: payload.enabled },
            };
        }

        case REMOTE_VERSION_CREATED: {
            const payload = action.payload as RemoteVersionCreatedPayload;
            // Advance latestVersionId only — the viewer's appVersionId stays put, so the workbench
            // goes read-only ("behind latest") rather than silently jumping to the new version.
            return {
                ...state,
                session: { ...state.session, latestVersionId: payload.appVersionId },
                workspace: {
                    ...state.workspace,
                    remoteVersionNotice: { version: payload.version, byName: payload.byName },
                    versionListRefreshKey: state.workspace.versionListRefreshKey + 1,
                },
            };
        }

        case DISMISS_REMOTE_VERSION_NOTICE:
            if (state.workspace.remoteVersionNotice === null) return state;
            return {
                ...state,
                workspace: { ...state.workspace, remoteVersionNotice: null },
            };

        case TEXT_COMMIT_STARTED:
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    pendingTextCommits: state.workspace.pendingTextCommits + 1,
                },
            };

        case TEXT_COMMIT_FINISHED:
            return {
                ...state,
                workspace: {
                    // Clamped: a stray finish (e.g. after a conversation reset zeroed the count)
                    // must never drive this negative and wedge the header in a saving state.
                    ...state.workspace,
                    pendingTextCommits: Math.max(0, state.workspace.pendingTextCommits - 1),
                },
            };

        default:
            return state;
    }
};
